import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyBrandingState,
  initialAcademyBrandingState,
} from './entities/academy-branding-store.model';
import { TenantBrandingCreateRequest, TenantBrandingResponse } from './entities/academy.model';
import { ODataQuery } from './entities/academy-query.model';
import { BrandingService } from './infra/branding.service';

const ALL_OPS = ['loadHostBranding', 'loadTenantBrandings', 'createTenantBranding'] as const;

export const AcademyBrandingStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyBrandingState>(initialAcademyBrandingState),
  withReqState(...ALL_OPS),
  withComputed((store) => ({
    tenantBrandings: computed<TenantBrandingResponse[]>(() => {
      const byId = store.tenantBrandingById();
      return store
        .tenantBrandingIds()
        .map((id) => byId[id])
        .filter((b): b is TenantBrandingResponse => !!b);
    }),
  })),
  withMethods(
    (
      store,
      branding = inject(BrandingService),
      errorStore = inject(ErrorStore),
    ) => {
      const loadHostBranding = rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadHostBranding'))),
          switchMap(() =>
            branding.getHostBranding().pipe(
              tapResponse({
                next: (hostBranding) =>
                  patchState(store, { hostBranding }, setPropLoaded('loadHostBranding')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadHostBranding', error));
                },
              }),
            ),
          ),
        ),
      );

      const loadTenantBrandings = rxMethod<ODataQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadTenantBrandings'))),
          switchMap((query) =>
            branding.listTenantBrandings(query || {}).pipe(
              tapResponse({
                next: (page) => {
                  const byId = { ...store.tenantBrandingById() };
                  for (const item of page.value) {
                    byId[item.TenantBrandingId] = item;
                  }
                  patchState(
                    store,
                    {
                      tenantBrandingById: byId,
                      tenantBrandingIds: page.value.map((i) => i.TenantBrandingId),
                    },
                    setPropLoaded('loadTenantBrandings'),
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadTenantBrandings', error));
                },
              }),
            ),
          ),
        ),
      );

      const createTenantBranding = rxMethod<TenantBrandingCreateRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('createTenantBranding'))),
          switchMap((vm) =>
            branding.createTenantBranding(vm).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      tenantBrandingById: {
                        ...store.tenantBrandingById(),
                        [created.TenantBrandingId]: created,
                      },
                      tenantBrandingIds: [
                        created.TenantBrandingId,
                        ...store.tenantBrandingIds(),
                      ],
                    },
                    setPropLoaded('createTenantBranding'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createTenantBranding', error));
                },
              }),
            ),
          ),
        ),
      );

      const reset = (): void =>
        patchState(store, initialAcademyBrandingState, setPropInit(...ALL_OPS));

      return { loadHostBranding, loadTenantBrandings, createTenantBranding, reset };
    },
  ),
);
